import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import type { ResolvedInput } from '../inputs/selectUserInputs';

/**
 * The curated "what the author actually wrote" list for the rating view.
 *
 * Renders registry-mapped fields first, then anything unrecognised behind a collapsed
 * "Other inputs" disclosure. The disclosure is the safety net: a form gaining a field shows up
 * here rather than vanishing, so a rater is never judging an output against a partial brief.
 */

interface UserInputsSectionProps {
  mapped: ResolvedInput[];
  other: ResolvedInput[];
}

const PRIMITIVE_TYPES = new Set(['string', 'number', 'boolean']);

const renderScalar = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const InputValue: React.FC<{ value: unknown }> = ({ value }) => {
  if (PRIMITIVE_TYPES.has(typeof value)) {
    return (
      <p className="text-[13px] text-gray-900 leading-relaxed whitespace-pre-wrap break-words">
        {renderScalar(value)}
      </p>
    );
  }

  if (Array.isArray(value)) {
    // Arrays of scalars are the common shape (claims, reasons to believe, marketing copies).
    // Anything richer is stringified rather than guessed at, so nothing is silently lost.
    return (
      <ul className="space-y-1.5">
        {value.map((item, i) => (
          <li key={i} className="text-[13px] text-gray-900 flex gap-2 leading-relaxed">
            <span className="text-blue-600 font-bold shrink-0" aria-hidden="true">
              ·
            </span>
            <span className="break-words min-w-0">
              {PRIMITIVE_TYPES.has(typeof item) ? renderScalar(item) : JSON.stringify(item)}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <pre className="text-[12px] text-gray-700 bg-gray-50 rounded-md p-2 overflow-x-auto">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
};

const InputRow: React.FC<{ entry: ResolvedInput }> = ({ entry }) => (
  <div className="px-4 py-3 border-b border-gray-100 last:border-b-0">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
      {entry.label}
    </p>
    <InputValue value={entry.value} />
  </div>
);

const UserInputsSection: React.FC<UserInputsSectionProps> = ({ mapped, other }) => {
  const [showOther, setShowOther] = useState(false);

  if (mapped.length === 0 && other.length === 0) {
    return (
      <p className="px-4 py-4 text-[13px] text-gray-500">
        This simulation recorded no author-provided inputs.
      </p>
    );
  }

  return (
    <div>
      {mapped.map((entry) => (
        <InputRow key={`${entry.label}-${entry.key}`} entry={entry} />
      ))}

      {other.length > 0 && (
        <div className="border-t border-gray-100">
          <button
            type="button"
            onClick={() => setShowOther((v) => !v)}
            aria-expanded={showOther}
            className="w-full flex items-center gap-1.5 px-4 py-3 text-[11px] font-semibold
                       text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ChevronRight
              className={`h-3.5 w-3.5 transition-transform ${showOther ? 'rotate-90' : ''}`}
              aria-hidden="true"
            />
            Other inputs ({other.length})
          </button>
          {showOther && (
            <div className="pb-1">
              {other.map((entry) => (
                <InputRow key={`other-${entry.key}`} entry={entry} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserInputsSection;
